import EventSchedule20 from '@carbon/icons-react/es/event--schedule/20';
import Event20 from '@carbon/icons-react/es/event/20';
import { Tabs, Space as SpaceAtom, Tooltip } from 'antd';
import moment, { Moment } from 'moment';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ProjectId } from 'api/models';
import { ReactComponent as Completed } from 'assets/svgs/export-completed.svg';
import { ReactComponent as InProgress } from 'assets/svgs/export-inprogress.svg';
import { DatePicker as DatePickerAtom } from 'components/atoms/date-picker';
import { API_DATE_FORMAT } from 'components/atoms/due-date-picker';
import { ColumnHeader } from 'components/atoms/table/column-header';
import { toastService } from 'components/molecules/toast';
import { StyledTable } from 'components/organisms/module-settings/key-events-and-intervals/settings/styles';
import { KeyEventPerformance } from 'components/pages/intervals-report/key-events-view/utils';
import { useProjectDetail } from 'data/projects';
import { useTranslation } from 'hooks/use-translation';
import { useAllFieldsQuery } from 'queries';
import { useUpdateProjectMutation } from 'queries/project';
import { useProjectFields } from 'utils/project-fields/use-project-fields';
import { EmptyMessage as IntervalsEmpty } from '../intervals/_card-empty';
import { EmptyMessage } from './_card-empty';

const StyledTabs = styled(Tabs)`
  margin: auto;
`;

const DatePicker = styled(DatePickerAtom)`
  border: none;
  background-color: transparent;
  width: 9rem;
`;

const KeyEventsTable = styled(StyledTable)`
  margin: 0 !important;

  .ant-table-pagination {
    display: none;
  }

  .ant-table-thead > tr > th {
    border: none;
    text-align: left;
    padding-left: 1rem !important;
  }

  .ant-table-container table > thead > tr:first-child {
    padding-left: 0 !important;
  }

  .ant-table-cell.ant-table-selection-column {
    border-spacing: initial;
  }

  .ant-table-thead > tr > th {
    border: none;
    text-align: left;
  }

  .ant-table-body > table > tbody > tr > td:first-child {
    padding-left: var(--spacing-05);
  }

  .ant-table-body > table > tbody > tr > td:last-child {
    padding-left: var(--spacing-05);
  }

  .ant-table-cell-fix-left {
    text-align: left !important;
  }

  .ant-table-tbody > tr > td {
    text-align: left;
    padding: var(--spacing-01);
    justify-content: left;
  }
`;

const IntervalTable = styled(StyledTable)`
  margin: 0 !important;
  .ant-table-container table > thead > tr:first-child th:first-child {
    padding-left: 1rem !important;
  }

  .ant-table-container table > thead > tr:first-child th:last-child {
    padding-left: 1rem !important;
  }
  .ant-table-tbody > tr > td {
    padding: var(--spacing-04);
  }
`;

export const Content = ({ projectId }) => {
  const { data: projectFields } = useProjectFields();
  const { data: projectData } = useProjectDetail(ProjectId(projectId));
  const projectKeyEvents = projectFields.filter(
    (field) => field.fieldType === 'KEY_EVENTS',
  );
  const projectIntervals = projectFields.filter(
    (field) => field.fieldType === 'INTERVALS',
  );

  const { mutate } = useUpdateProjectMutation(ProjectId(projectId));
  const errorMessage = useTranslation('error');
  const errorTitle = useTranslation('error_saving');

  const handleOnCustomFieldChange = (fieldValue, fieldId) => {
    mutate(
      { fields: [{ fieldId, fieldValue }] },
      {
        onError: () => {
          toastService({
            title: errorTitle,
            message: errorMessage,
          }).error();
        },
      },
    );
  };

  const { TabPane } = Tabs;

  const columns = [
    { title: 'Name of key event', dataIndex: 'name', key: '' },
    {
      title: 'Planned date',
      dataIndex: 'plannedDate',
      key: '',
      editable: true,
    },
    {
      title: 'Activity date',
      dataIndex: 'activityDate',
      key: '',
      editable: true,
    },
    {
      title: (
        <ColumnHeader
          title="Performance"
          description="Calculated based on working days. Weekends will be skipped."
        />
      ),
      description: useTranslation('reports.columns.end_date.delay.tooltip'),
      dataIndex: 'performance',
      key: '',
    },
  ];

  const intervalColumns = [
    { title: 'Name of Interval', dataIndex: 'name', key: '' },
    { title: 'Status', dataIndex: 'status', key: '' },
    { title: 'Planned Duration', dataIndex: 'plannedDuration', key: '' },
    { title: 'Actual Duration', dataIndex: 'actualDuration', key: '' },
  ];
  const { data: allFields } = useAllFieldsQuery();
  const events =
    allFields?.filter(
      (fieldItem) =>
        fieldItem.fieldType === 'KEY_EVENTS' &&
        fieldItem?.metaData?.relatedFieldId,
    ) ?? [];

  const allEvents =
    allFields?.filter((fieldItem) => fieldItem.fieldType === 'KEY_EVENTS') ??
    [];

  const intervals =
    allFields?.filter(
      (fieldItem) =>
        fieldItem.fieldType === 'INTERVALS' &&
        fieldItem?.metaData?.relatedFieldId,
    ) ?? [];

  const relatedFieldMapping = events.reduce((acc, curr) => {
    return { ...acc, [curr.fieldId]: curr?.metaData?.relatedFieldId };
  }, {});

  const relatedIntervalMapping = intervals.reduce((acc, curr) => {
    return { ...acc, [curr.fieldId]: curr?.metaData?.relatedFieldId };
  }, {});

  const _dataSource = events.reduce<{
    eventsWithBothPlannedAndActualDates: any[];
    eventsWithPlannedDateOnly: any[];
    eventsWithActualDateOnly: any[];
    eventsWithNeitherPlannedOrActualDates: any[];
  }>(
    (acc, event) => {
      const name = event.fieldLabel;
      const activityDate =
        projectData?.fields.find((field) => field.fieldId === event.fieldId)
          ?.fieldValue ?? null;
      const plannedDate =
        projectData?.fields.find(
          (field) => field.fieldId === relatedFieldMapping[event.fieldId],
        )?.fieldValue ?? null;

      const _onActivityDateChangeHandler = (
        response: moment.Moment | null,
      ): void => {
        handleOnCustomFieldChange(
          response ? response.format(API_DATE_FORMAT) : response,
          event.fieldId,
        );
      };

      const _onPlannedDateChangeHandler = (
        response: moment.Moment | null,
      ): void => {
        handleOnCustomFieldChange(
          response ? response.format(API_DATE_FORMAT) : response,
          relatedFieldMapping[event.fieldId],
        );
      };

      const rowData = {
        name,
        activityDate: (
          <DatePicker
            value={activityDate ? moment(activityDate as any as Moment) : null}
            allowClear
            disabled={
              !allEvents.find((field) => field.fieldId === event.fieldId)
                ?.metaData?.allowAdminsAndOwnersToEdit
            }
            onChange={_onActivityDateChangeHandler}
          />
        ),
        plannedDate: (
          <DatePicker
            value={plannedDate ? moment(plannedDate as any as Moment) : null}
            allowClear
            disabled={
              !allEvents.find((field) => field.fieldId === event.fieldId)
                ?.metaData?.allowAdminsAndOwnersToEdit
            }
            onChange={_onPlannedDateChangeHandler}
          />
        ),
        performance: (
          <KeyEventPerformance
            plannedDate={plannedDate}
            activityDate={activityDate}
          />
        ),
      };

      if (activityDate && plannedDate) {
        return {
          ...acc,
          eventsWithBothPlannedAndActualDates: [
            ...acc.eventsWithBothPlannedAndActualDates,
            rowData,
          ],
        };
      }
      if (plannedDate && !activityDate) {
        return {
          ...acc,
          eventsWithPlannedDateOnly: [
            ...acc.eventsWithPlannedDateOnly,
            rowData,
          ],
        };
      }
      if (!plannedDate && activityDate) {
        return {
          ...acc,
          eventsWithActualDateOnly: [...acc.eventsWithActualDateOnly, rowData],
        };
      }
      if (!activityDate && !plannedDate) {
        return {
          ...acc,
          eventsWithNeitherPlannedOrActualDates: [
            ...acc.eventsWithNeitherPlannedOrActualDates,
            rowData,
          ],
        };
      }

      return acc;
    },
    {
      eventsWithBothPlannedAndActualDates: [],
      eventsWithPlannedDateOnly: [],
      eventsWithActualDateOnly: [],
      eventsWithNeitherPlannedOrActualDates: [],
    },
  );

  /* doing this so that key events are grouped based on the
     availability of planned and actual values and ordered accordingly
  */
  const dataSource = [
    ..._dataSource.eventsWithBothPlannedAndActualDates,
    ..._dataSource.eventsWithPlannedDateOnly,
    ..._dataSource.eventsWithActualDateOnly,
    ..._dataSource.eventsWithNeitherPlannedOrActualDates,
  ];

  const intervalDataSource = intervals.map((interval) => {
    const actualDurationValue =
      projectData?.fields.find((field) => field.fieldId === interval.fieldId)
        ?.fieldValue ?? '-';
    const plannedDurationValue =
      projectData?.fields.find(
        (field) => field.fieldId === relatedIntervalMapping[interval.fieldId],
      )?.fieldValue ?? '-';
    const plannedDuration =
      plannedDurationValue === 0 ? '-' : plannedDurationValue;

    const actualDuration =
      !plannedDuration && !actualDurationValue ? (
        '-'
      ) : actualDurationValue === 0 ? (
        <span>-</span>
      ) : Number(plannedDuration) > Number(actualDurationValue) ||
        plannedDuration === actualDurationValue ? (
        <span style={{ color: 'var(--green-50)' }}>{actualDurationValue}</span>
      ) : Number(plannedDuration) < Number(actualDurationValue) ? (
        <span style={{ color: 'var(--red-60)' }}>{actualDurationValue}</span>
      ) : (
        <span>{actualDurationValue ?? '-'}</span>
      );

    let status;

    const startKeyEventValue = interval.metaData?.startKeyEventId
      ? projectData?.fields.find(
          (field) => field.fieldId === interval.metaData?.startKeyEventId,
        )?.fieldValue
      : null;
    const endKeyEventValue = interval.metaData?.endKeyEventId
      ? projectData?.fields.find(
          (field) => field.fieldId === interval.metaData?.endKeyEventId,
        )?.fieldValue
      : null;

    if (startKeyEventValue && endKeyEventValue) {
      status = 'Completed';
    } else if (startKeyEventValue || endKeyEventValue) {
      status = 'In-Progress';
    } else if (!startKeyEventValue && !endKeyEventValue) {
      status = 'Todo';
    }

    const statusValue = (
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-start',
          gap: 'var(--spacing-02)',
        }}
      >
        {status === 'In-Progress' ? (
          <InProgress />
        ) : status === 'Completed' ? (
          <Completed />
        ) : status === 'Todo' ? (
          <></>
        ) : null}
        <div>{status === 'Todo' ? '-' : status}</div>
      </div>
    );

    const IntervalTooltip = ({ keyEventNames }) => (
      <SpaceAtom direction="vertical">
        <FormattedMessage id="project.overview.interval_tooltip.title" />
        <strong>
          <FormattedMessage
            id="project.overview.interval_tooltip.description"
            values={{
              firstInterval: keyEventNames[0],
              secondInterval: keyEventNames[1],
            }}
          />
        </strong>
      </SpaceAtom>
    );

    const IntervalWithTooltip = ({ keyEventNames, intervalName }) => {
      return (
        <Tooltip title={<IntervalTooltip keyEventNames={keyEventNames} />}>
          {intervalName}
        </Tooltip>
      );
    };

    return {
      name: (
        <IntervalWithTooltip
          keyEventNames={[
            events.find(
              (event) => event.fieldId === interval.metaData?.startKeyEventId,
            )?.fieldLabel,
            events.find(
              (event) => event.fieldId === interval.metaData?.endKeyEventId,
            )?.fieldLabel,
          ]}
          intervalName={interval.fieldLabel}
        />
      ),
      plannedDuration,
      actualDuration,
      status: statusValue,
    };
  });
  const returnValue = (
    <>
      <StyledTabs>
        <TabPane
          tab={
            <div style={{ display: 'flex', gap: '8px' }}>
              <Event20 />
              <FormattedMessage id="key_events" />
            </div>
          }
          key="keyEvents"
        >
          {projectKeyEvents.length > 0 ? (
            <KeyEventsTable
              dataSource={dataSource}
              columns={columns}
              pagination={false}
              scroll={{ x: 700, y: 300 }}
              bordered
            />
          ) : (
            <EmptyMessage />
          )}
        </TabPane>
        <TabPane
          tab={
            <div style={{ display: 'flex', gap: '8px' }}>
              <EventSchedule20 />
              <FormattedMessage id="intervals" />
            </div>
          }
          key="intervals"
        >
          {projectIntervals.length > 0 ? (
            <IntervalTable
              columns={intervalColumns}
              dataSource={intervalDataSource}
              pagination={false}
              scroll={{ x: 700, y: 300 }}
              bordered
            />
          ) : (
            <IntervalsEmpty />
          )}
        </TabPane>
      </StyledTabs>
    </>
  );

  return returnValue;
};
